// pickServerWithData — which of an organization's servers the wizard resumes on.
//
// An organization can own several servers, but channels and banks hang off
// exactly one of them. Resuming on whichever server the list returns first
// shows an empty Channels and Banks step whenever that server is not the one
// holding the data, while the steps still read as complete (an ACTIVE org is
// marked complete without looking at any data).
//
// The caller probes each server for whether it has any banks or channels and
// hands the answers over in the order the servers were listed. Banks outrank
// channels because a bank cannot exist without its channel and the Banks step
// is the one an operator resumes for; a tie keeps the earliest listed server,
// so an org whose servers are all empty resumes where it always did.

export interface ServerDataProbe {
  serverId: string;
  hasBanks: boolean;
  hasChannels: boolean;
}

function dataRank(probe: ServerDataProbe): number {
  if (probe.hasBanks) return 2;
  return probe.hasChannels ? 1 : 0;
}

/**
 * The id of the server holding the most configuration, or null when `probes`
 * is empty. Probes are in listed order; the earliest wins a tie.
 */
export default function pickServerWithData(probes: readonly ServerDataProbe[]): string | null {
  let best: ServerDataProbe | null = null;
  probes.forEach((probe) => {
    if (best === null || dataRank(probe) > dataRank(best)) best = probe;
  });
  return best === null ? null : (best as ServerDataProbe).serverId;
}
